import { ProfileRoles } from '@bp/pim-auth-constants';
import { AddIcon, Grid, GridColumn, GridRow, LazyLoader, Modal, Tab } from '@bp/ui-components';
import classNames from 'classnames';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import { graphql } from '../../client/gql';
import { AnnouncementForm, AnnouncementFormType } from '../../components/AnnouncementForm/AnnouncementForm';
import { BpCard } from '../../components/BpCard/BpCard';
import { BpHeader } from '../../components/BpHeader/BpHeader';
import { BpPage } from '../../components/BpPage/BpPage';
import { useGetAnnouncements } from '../../hooks/matrix/useGetAnnouncements';
import { useGetMatrixRoomByName } from '../../hooks/matrix/useGetMatrixRoomByName';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { backendApi } from '../../utils/backendApi';
import { getDaytimeGreeting } from '../../utils/dateCalculations';
import { NotificationReason, RoomNames } from '../../utils/matrixClient';
import { DashboardEvents } from '../../components/Home/DashboardEvents';
import styles from './Home.module.scss';
import { PreviewData } from '../../components/Home/PreviewData';
import { GroupSubject } from '@bp/pim-auth-permissions';
import { showErrorToast } from '../../utils/showErrorToast';
import { LinkList } from '../../components/LinkList/LinkList';
import { OpenAssignmentsOverview } from 'components/OpenAssignmentsOverview/OpenAssignmentsOverview';
import { ListsBar } from '../../components/ListsBar/ListsBar';
import { AnnouncementsList } from 'components/AnnouncementsList/AnnouncementsList';
import { AppointmentForm } from 'components/AppointmentForm/AppointmentForm';

const HomePageQueryDocument = graphql(`
  query HomePageData(
    $submissionsWhere: SubmissionWhere!
    $organizations: [ID!]!
    $profile: ID!
    $calendarEventWhere: CalendarEventWhere!
  ) {
    ...OpenAssignmentQueryFields
    ...PermissionCheckerFields
    ...PreviewDataQueryFields
    ...DashboardEventsQueryFields
    ...DashboardEventsFields
    ...LinkListFields
  }
`);

export const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuids = pimAuthClaims.getAllowedOrganizationUuids();

  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const organization = pimAuthClaims.getOrganization();

  const [{ data }] = useQuery({
    query: HomePageQueryDocument,
    variables: {
      organizations: organizationUuids,
      profile: profileUuid,
      calendarEventWhere: {
        OR: [
          {
            holderConnection: {
              Organization: {
                node: { uuid: organization.uuid },
              },
            },
          },
          {
            attendees_SOME: {
              uuid: profileUuid,
            },
          },
          {
            owner: {
              uuid: profileUuid,
            },
          },
        ],
      },
      submissionsWhere: { owner: { uuid: profileUuid } },
    },
  });
  const permissions = usePermissionChecker(data);
  const checkIsCourseEditor = (group: GroupSubject) => permissions?.isCourseEditor(group);

  const firstName = pimAuthClaims.getProfile()?.firstName ?? '';

  const { getRoom } = useGetMatrixRoomByName();
  const room = getRoom(organizationUuid, RoomNames.Organization);
  const announcements = useGetAnnouncements(room);

  const handleAnnouncementFormSubmit = async (values?: AnnouncementFormType) => {
    if (values) {
      setLoading(true);
      const resp = await backendApi.notify({
        type: NotificationReason.OrganizationAnnouncement,
        content: values.message,
        title: values.title,
        organizationUuid: organizationUuid,
      });

      if (resp.ok) setAnnouncementModalOpen(false);

      if (!resp.ok) showErrorToast({ name: '', graphQLErrors: [], message: resp.statusText });

      setLoading(false);
    }
  };

  const teacherTabs = [
    {
      title: t('common.calendar'),
      value: 'calendar',
      content: (
        <DashboardEvents
          query={data}
          isOrganizationAdmin={permissions?.isOrganizationAdmin(organization) ?? false}
          profileUuid={profileUuid}
          onAdd={() => setAppointmentModalOpen(true)}
        />
      ),
    },
    {
      content: (
        <div className={classNames(styles.tab, styles['timetable-tab'])}>
          <img src='./timetable-mockup.png' alt={'Beispiel Stundenplan'} />
        </div>
      ),
      title: t('common.timetable'),
      value: 'timetable',
    },
  ];

  const studentTabs = [
    {
      content: (
        <div className={classNames(styles.tab, styles['gantt-tab'])}>
          <OpenAssignmentsOverview
            query={data}
            onNavigate={async (courseUuid, uuid) => {
              navigate(`/courses/${courseUuid}/assignments/${uuid}`);
            }}
          />
        </div>
      ),
      title: t('assignments.title'),
      value: 'assignments',
    },
    {
      content: (
        <div className={classNames(styles.tab, styles['timetable-tab'])}>
          <img src='./timetable-mockup.png' alt={'Beispiel Stundenplan'} />
        </div>
      ),
      title: t('common.timetable'),
      value: 'timetable',
    },
  ];

  const classes = classNames(styles.home, {
    [styles['no-bar']]: !permissions?.canCreateCourse(),
  });

  return (
    <BpPage
      noHeadlineMargin
      className={classes}
      title=''
      isForbidden={false}
      organization={pimAuthClaims.getOrganization()}
    >
      <BpHeader breakLine headline={getDaytimeGreeting(firstName)} size='l' noMargin={permissions?.canCreateCourse()} />
      {permissions?.canCreateCourse() && (
        <Suspense fallback={<LazyLoader embedded transparent />}>
          <ListsBar />
        </Suspense>
      )}
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)'>
          <PreviewData isCourseEditor={checkIsCourseEditor} query={data} />
          <GridColumn width={8}>
            <GridRow spacingTop='none'>
              <BpCard className={styles['calendar-card']} noPadding minHeight='584px'>
                <Suspense fallback={<LazyLoader embedded transparent />}>
                  <Tab
                    className={styles['tab-header']}
                    tabs={
                      permissions?.hasOrganizationRole(ProfileRoles.Student, pimAuthClaims.getOrganization())
                        ? studentTabs
                        : teacherTabs
                    }
                  />
                </Suspense>
              </BpCard>
            </GridRow>
            <GridRow mobileGap='var(--grid-column-gap)'>
              <GridColumn width={6}>
                <BpCard
                  noPadding
                  fixedHeight='280px'
                  canScroll
                  header={{
                    headline: t('announcements.title'),
                    actions: permissions?.canCreateOrganizationAnnouncment({ uuid: organizationUuid })
                      ? [
                          {
                            text: t('common.add'),
                            icon: <AddIcon />,
                            callback: () => {
                              setAnnouncementModalOpen(true);
                            },
                          },
                        ]
                      : [],
                  }}
                >
                  <Suspense fallback={<LazyLoader embedded transparent />}>
                    <AnnouncementsList
                      emptyStateSettings={{ hideIcon: true, forcedHeight: '' }}
                      announcements={announcements}
                      hasPermissionToDelete={
                        permissions?.canCreateOrganizationAnnouncment({
                          uuid: organizationUuid,
                        }) ?? false
                      }
                    />
                  </Suspense>
                </BpCard>
              </GridColumn>
              <GridColumn width={6}>
                <BpCard fixedHeight='280px' header={{ headline: t('links.title') }}>
                  <Suspense fallback={<LazyLoader embedded transparent />}>
                    <LinkList query={data} />
                  </Suspense>
                </BpCard>
              </GridColumn>
            </GridRow>
          </GridColumn>
        </GridRow>
      </Grid>

      <Modal
        isOpen={appointmentModalOpen}
        onRequestClose={() => setAppointmentModalOpen(false)}
        isFormModal
        hideHeader
        hideFooter
      >
        <Suspense fallback={<LazyLoader embedded transparent forceHeight='40vh' />}>
          <AppointmentForm
            organizationUuid={organizationUuid}
            holderUuid={organizationUuid}
            conferenceContext='Organization'
            isModal
            onClose={() => setAppointmentModalOpen(false)}
          />
        </Suspense>
      </Modal>

      <Modal
        isOpen={announcementModalOpen}
        onRequestClose={() => setAnnouncementModalOpen(false)}
        isFormModal
        hideFooter
        title={t('announcements.new')}
        width='s'
      >
        <Suspense fallback={<LazyLoader embedded transparent />}>
          <AnnouncementForm
            onSubmit={handleAnnouncementFormSubmit}
            loading={loading}
            closeModal={() => setAnnouncementModalOpen(false)}
          />
        </Suspense>
      </Modal>
    </BpPage>
  );
};
